import { useState, type FormEvent } from 'react';
import { api, mensajeDe, type Categoria, type ParejaAdmin, type ResultadoCarga } from '../../services/api';
import { Aviso, Boton, Campo, ENTRADA, SelectorTema, Tarjeta, Titulo, Vacio } from '../../components/ui';
import { NOMBRE_TEMA, type Tema } from '../../nucleo/temas';
import { SeccionGrupos } from './Grupos';
import { resolverCuadroGuardado } from '../../nucleo/vistaCuadro';
import { SeccionCuadro } from './Cuadro';
import { FormPremios } from './Premios';
import { Desplegable } from '../../components/Desplegable';
import {
  NOMBRE_FORMATO,
  PASAN_MAX,
  validarCategoria,
  type DatosCategoria,
  type Formato,
} from '../../nucleo/validar';

// ── Formulario de categoría ───────────────────────────────────────────────

export function FormCategoria({
  inicial,
  textoBoton,
  alGuardar,
  alCancelar,
}: {
  inicial?: DatosCategoria;
  textoBoton: string;
  alGuardar: (d: DatosCategoria) => Promise<void>;
  alCancelar?: () => void;
}) {
  const [nombre, setNombre] = useState(inicial?.nombre ?? '');
  const [formato, setFormato] = useState<Formato>(inicial?.formato ?? 'grupos');
  const [pasan, setPasan] = useState(inicial?.pasan_por_grupo ?? 2);
  const [tema, setTema] = useState<Tema | null>(inicial?.tema ?? null);
  const [cupo, setCupo] = useState(inicial?.cupo?.toString() ?? '');
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    const datos = validarCategoria({ nombre, formato, pasan_por_grupo: pasan, tema, cupo: cupo ? Number(cupo) : null });
    if (!datos.ok) return setError(datos.error);
    setError('');
    setEnviando(true);
    try {
      await alGuardar(datos.valor);
    } catch (err) {
      setError(mensajeDe(err));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <form onSubmit={enviar} className="flex flex-col gap-3">
      <Campo etiqueta="Nombre">
        <input className={ENTRADA} value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="5ta Caballeros" />
      </Campo>
      <Campo etiqueta="Formato">
        <div className="grid grid-cols-2 gap-2">
          {(Object.keys(NOMBRE_FORMATO) as Formato[]).map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={formato === f}
              onClick={() => setFormato(f)}
              className={`min-h-11 rounded-xl border text-sm font-semibold ${
                formato === f ? 'border-(--color-acento) text-(--color-acento)' : 'border-(--color-borde) text-(--color-apagado)'
              }`}
            >
              {NOMBRE_FORMATO[f]}
            </button>
          ))}
        </div>
      </Campo>
      {formato === 'grupos' && (
        <Campo etiqueta="Pasan al cuadro" ayuda="El tamaño de los grupos lo elegís al armarlos, cuando sepas cuántas parejas se anotaron.">
          <select className={ENTRADA} value={pasan} onChange={(e) => setPasan(Number(e.target.value))}>
            {Array.from({ length: PASAN_MAX }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>{n} por grupo</option>
            ))}
          </select>
        </Campo>
      )}
      <Campo etiqueta="Cupo de parejas (opcional)" ayuda="Para llevar la cuenta: «23 de 32». Si se pasa, la app avisa pero deja seguir. El público no lo ve.">
        <input className={ENTRADA} inputMode="numeric" value={cupo} placeholder="sin cupo" onChange={(e) => setCupo(e.target.value.replace(/\D/g, '').slice(0, 3))} />
      </Campo>
      <Campo etiqueta="Colores">
        <SelectorTema valor={tema} onChange={setTema} conHeredar />
      </Campo>
      {error && <Aviso tono="error">{error}</Aviso>}
      <div className="flex gap-2">
        <Boton type="submit" deshabilitado={enviando}>
          {enviando ? 'Guardando...' : textoBoton}
        </Boton>
        {alCancelar && (
          <Boton tipo="secundario" onClick={alCancelar}>
            Cancelar
          </Boton>
        )}
      </div>
    </form>
  );
}

// ── Tarjeta de una categoría ──────────────────────────────────────────────

/** "23 de 32 parejas", o "23 parejas" si no hay cupo. */
export function textoDeCupo(inscriptas: number, cupo: number | null | undefined): string {
  return cupo ? `${inscriptas} de ${cupo} parejas` : `${inscriptas} pareja${inscriptas === 1 ? '' : 's'}`;
}

export function TarjetaCategoria({
  premiosActivos = true,
  horariosActivos = true,
  categoria,
  temaTorneo,
  recargar,
  abierta,
  alAlternar,
}: {
  categoria: Categoria<ParejaAdmin>;
  premiosActivos?: boolean;
  horariosActivos?: boolean;
  temaTorneo: Tema;
  recargar: () => Promise<void>;
  /** Con muchas categorías la pantalla es un acordeón: se abre de a una. */
  abierta: boolean;
  /** undefined = no se pliega (hay pocas categorías). */
  alAlternar?: () => void;
}) {
  const [editando, setEditando] = useState(false);
  const [verBajas, setVerBajas] = useState(false);
  const [error, setError] = useState('');

  const inscriptas = categoria.parejas.filter((p) => p.estado === 'inscripta');
  const bajas = categoria.parejas.filter((p) => p.estado === 'baja');

  const cambiarEstado = async (p: ParejaAdmin, estado: 'inscripta' | 'baja') => {
    try {
      await api.estadoPareja(p.id, estado);
      setError('');
      await recargar();
    } catch (e) {
      setError(mensajeDe(e));
    }
  };

  const cupo = categoria.cupo ?? null;
  const pasada = cupo !== null && inscriptas.length > cupo;
  // Los partidos que se juegan de verdad: en el cuadro, los cruces donde una
  // pareja pasa directo tienen su fila pero no se juegan, y no cuentan.
  const noSeJuegan = new Set(
    categoria.cuadro
      ? resolverCuadroGuardado(categoria.cuadro, categoria.grupos, new Set(bajas.map((p) => p.id)), categoria.pasan_por_grupo ?? 1)
          .rondas.flat()
          .filter((c) => c.pasaDirecto)
          .map((c) => c.id)
      : [],
  );
  const partidos = [...categoria.grupos.flatMap((g) => g.partidos), ...(categoria.cuadro?.partidos ?? [])].filter((p) => !noSeJuegan.has(p.id));
  const jugados = partidos.filter((p) => p.estado === 'jugado').length;

  const resumen = [
    NOMBRE_FORMATO[categoria.formato],
    categoria.formato === 'grupos' ? `pasan ${categoria.pasan_por_grupo} por grupo` : null,
    categoria.grupos.length ? `${categoria.grupos.length} grupos armados` : null,
    categoria.cuadro ? 'cuadro armado' : null,
    categoria.tema ? `colores ${NOMBRE_TEMA[categoria.tema]}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    // La tarjeta se pinta con el tema de la categoría: así se ve cuál la pisa.
    <div data-tema={categoria.tema ?? temaTorneo}>
      <Tarjeta className="border-l-4 border-l-(--color-acento)">
        <div className="mb-1 flex items-start gap-2">
          <button
            type="button"
            className="min-w-0 flex-1 text-left disabled:cursor-default"
            onClick={alAlternar}
            disabled={!alAlternar}
            aria-expanded={abierta}
          >
            <Titulo extra={<span className={pasada ? 'font-semibold text-(--color-alerta)' : ''}>{textoDeCupo(inscriptas.length, cupo)}</span>}>
              {alAlternar && <span className="text-base text-(--color-apagado)">{abierta ? '▾' : '▸'}</span>}
              {categoria.nombre}
            </Titulo>
            <p className="-mt-2 text-xs text-(--color-apagado)">
              {resumen}
              {partidos.length > 0 && ` · ${jugados} de ${partidos.length} partidos jugados`}
            </p>
          </button>
          {abierta && !editando && (
            <Boton chico tipo="texto" onClick={() => setEditando(true)}>
              Editar
            </Boton>
          )}
        </div>
        {pasada && (
          <p className="text-xs text-(--color-alerta)">Se pasó del cupo por {inscriptas.length - cupo!}. La app no lo impide: es para que lo tengas en cuenta.</p>
        )}
        {abierta && (
          <>

        {editando && (
          <div className="my-3 rounded-xl border border-(--color-borde) p-3">
            <FormCategoria
              inicial={{ ...categoria, cupo }}
              textoBoton="Guardar"
              alGuardar={async (d) => {
                await api.editarCategoria(categoria.id, d);
                setEditando(false);
                await recargar();
              }}
              alCancelar={() => setEditando(false)}
            />
          </div>
        )}

        {error && <Aviso tono="error">{error}</Aviso>}

        <div className="mt-3 flex flex-col gap-1.5">
        <Desplegable titulo="Parejas" resumen={`${textoDeCupo(inscriptas.length, cupo)}${bajas.length ? ` · ${bajas.length} de baja` : ''}`} abierta={categoria.parejas.length === 0}>
        {inscriptas.length === 0 ? (
          <Vacio>Sin parejas todavía. Pegá la lista acá abajo.</Vacio>
        ) : (
          <ol className="mt-3 flex flex-col">
            {inscriptas.map((p, i) => (
              <FilaPareja key={p.id} numero={i + 1} pareja={p} recargar={recargar} alBaja={() => {
                  // Con grupos armados, una baja es un abandono: que se sepa qué implica.
                  const aviso = `${p.jugador1} / ${p.jugador2} abandona: los partidos que le faltan cuentan como W.O. (6-0 6-0) para los rivales, y no clasifica. Lo ya jugado se mantiene. ¿Seguimos?`;
                  if ((categoria.grupos.length || categoria.cuadro) && !window.confirm(aviso)) return;
                  void cambiarEstado(p, 'baja');
                }} />
            ))}
          </ol>
        )}

        {bajas.length > 0 && (
          <div className="mt-2">
            <button className="text-xs text-(--color-apagado) underline" onClick={() => setVerBajas(!verBajas)}>
              {verBajas ? 'Ocultar' : 'Ver'} {bajas.length} dada{bajas.length === 1 ? '' : 's'} de baja
            </button>
            {verBajas && (
              <ul className="mt-2 flex flex-col gap-1">
                {bajas.map((p) => (
                  <li key={p.id} className="flex items-center gap-2 text-sm text-(--color-apagado)">
                    <span className="flex-1 line-through">
                      {p.jugador1} / {p.jugador2}
                    </span>
                    <Boton chico tipo="texto" onClick={() => cambiarEstado(p, 'inscripta')}>
                      Volver a inscribir
                    </Boton>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Con los grupos armados no se suman parejas: quedarían sin grupo. */}
        {categoria.grupos.length === 0 && !categoria.cuadro && (
          <CargarParejas categoriaId={categoria.id} recargar={recargar} inscriptas={inscriptas.length} cupo={cupo} />
        )}
        </Desplegable>

        {categoria.formato === 'grupos' && <Desplegable titulo="Grupos y resultados" resumen={categoria.grupos.length ? `${categoria.grupos.length} grupos armados` : 'Elegí cómo repartir las parejas'}>
          <SeccionGrupos horariosActivos={horariosActivos} categoria={categoria} recargar={recargar} />
        </Desplegable>}
        <Desplegable titulo="Cuadro y resultados" resumen={categoria.cuadro ? 'Cuadro armado' : 'Armá los cruces de eliminación'}>
          <SeccionCuadro categoria={categoria} recargar={recargar} />
        </Desplegable>
        {premiosActivos && <Desplegable titulo="Premios" resumen={categoria.premios.length ? `${categoria.premios.length} puestos con premio` : 'Definí qué se lleva cada puesto'}>
          <FormPremios categoria={categoria} recargar={recargar} />
        </Desplegable>}
        </div>
          </>
        )}
      </Tarjeta>
    </div>
  );
}

// ── Una pareja, con su edición en el lugar ────────────────────────────────

function FilaPareja({
  numero,
  pareja,
  recargar,
  alBaja,
}: {
  numero: number;
  pareja: ParejaAdmin;
  recargar: () => Promise<void>;
  alBaja: () => void;
}) {
  const [editando, setEditando] = useState(false);
  const [j1, setJ1] = useState(pareja.jugador1);
  const [j2, setJ2] = useState(pareja.jugador2);
  const [cabeza, setCabeza] = useState(pareja.cabeza_serie?.toString() ?? '');
  const [error, setError] = useState('');

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.editarPareja(pareja.id, { jugador1: j1, jugador2: j2, cabeza_serie: cabeza ? Number(cabeza) : null });
      setEditando(false);
      setError('');
      await recargar();
    } catch (err) {
      setError(mensajeDe(err));
    }
  };

  if (editando) {
    return (
      <li className="border-t border-(--color-borde) py-3 first:border-t-0">
        <form onSubmit={guardar} className="flex flex-col gap-2">
          <div className="grid grid-cols-[1fr_1fr_4.5rem] gap-2">
            <input className={ENTRADA} value={j1} onChange={(e) => setJ1(e.target.value)} aria-label="Jugador 1" />
            <input className={ENTRADA} value={j2} onChange={(e) => setJ2(e.target.value)} aria-label="Jugador 2" />
            <input
              className={ENTRADA}
              value={cabeza}
              inputMode="numeric"
              placeholder="CS"
              aria-label="Cabeza de serie"
              onChange={(e) => setCabeza(e.target.value.replace(/\D/g, '').slice(0, 2))}
            />
          </div>
          {error && <Aviso tono="error">{error}</Aviso>}
          <div className="flex gap-2">
            <Boton chico type="submit">Guardar</Boton>
            <Boton chico tipo="secundario" onClick={() => setEditando(false)}>Cancelar</Boton>
            <span className="flex-1" />
            <Boton chico tipo="peligro" onClick={alBaja}>Dar de baja</Boton>
          </div>
        </form>
      </li>
    );
  }

  return (
    <li className="flex items-center gap-3 border-t border-(--color-borde) py-2 first:border-t-0">
      <span className="w-6 text-right text-sm text-(--color-apagado) tabular-nums">{numero}</span>
      <span className="flex-1 font-semibold">
        {pareja.jugador1} / {pareja.jugador2}
      </span>
      {pareja.cabeza_serie !== null && (
        <span className="rounded-full border border-(--color-acento) px-2 text-xs font-bold text-(--color-acento)" title="Cabeza de serie">
          CS {pareja.cabeza_serie}
        </span>
      )}
      <Boton chico tipo="texto" onClick={() => setEditando(true)}>
        Editar
      </Boton>
    </li>
  );
}

// ── Pegar la lista ────────────────────────────────────────────────────────

/**
 * Dos pasos: "Revisar" muestra lo que se entendió sin guardar nada, y recién
 * "Guardar" las inscribe. Lo que no se entendió queda en el cuadro de texto
 * para corregirlo, y lo que se guardó sale de ahí.
 */
function CargarParejas({
  categoriaId,
  recargar,
  inscriptas,
  cupo,
}: {
  categoriaId: number;
  recargar: () => Promise<void>;
  inscriptas: number;
  cupo: number | null;
}) {
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState('');
  const [revision, setRevision] = useState<ResultadoCarga | null>(null);
  const [guardadas, setGuardadas] = useState(0);
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);

  const mandar = async (probar: boolean) => {
    setEnviando(true);
    setError('');
    try {
      const r = await api.cargarParejas(categoriaId, texto, probar);
      if (probar) {
        setRevision(r);
        setGuardadas(0);
      } else {
        setGuardadas(r.guardadas);
        setRevision(r.errores.length ? { ...r, parejas: [] } : null);
        // Quedan solo los renglones con problema, para corregirlos.
        const renglones = texto.split(/\r?\n/);
        setTexto(r.errores.map((e) => renglones[e.linea - 1] ?? '').join('\n'));
        await recargar();
      }
    } catch (e) {
      setError(mensajeDe(e));
    } finally {
      setEnviando(false);
    }
  };

  if (!abierto) {
    return (
      <div className="mt-3">
        <Boton chico tipo="secundario" onClick={() => setAbierto(true)}>
          + Pegar lista de parejas
        </Boton>
      </div>
    );
  }

  return (
    <div className="mt-4 flex flex-col gap-2 rounded-xl border border-(--color-borde) p-3">
      <Campo
        etiqueta="Pegá la lista, una pareja por renglón"
        ayuda="Ej: «1. Pérez / Gómez (1)». La numeración se ignora; (1) es cabeza de serie. También sirve «-», «y» o «&» entre los nombres."
      >
        <textarea
          className={`${ENTRADA} min-h-40 py-2 font-mono text-sm`}
          value={texto}
          onChange={(e) => {
            setTexto(e.target.value);
            setRevision(null);
          }}
          placeholder={'1. Ferrero / Castro (1)\n2. Molina / Sosa\n3. Ruiz / Paz'}
        />
      </Campo>

      {guardadas > 0 && <Aviso tono="bien">Se inscribieron {guardadas} pareja{guardadas === 1 ? '' : 's'}.</Aviso>}
      {error && <Aviso tono="error">{error}</Aviso>}

      {revision && (
        <div className="flex flex-col gap-2 text-sm">
          {revision.parejas.length > 0 && cupo !== null && inscriptas + revision.parejas.length > cupo && (
            <p className="font-semibold text-(--color-alerta)">
              Con estas quedan {inscriptas + revision.parejas.length} de {cupo}: se pasa del cupo por {inscriptas + revision.parejas.length - cupo}. Podés
              inscribirlas igual.
            </p>
          )}
          {revision.parejas.length > 0 && (
            <div>
              <p className="font-semibold text-(--color-acento)">Se van a inscribir {revision.parejas.length}:</p>
              <ul className="mt-1 text-(--color-apagado)">
                {revision.parejas.map((p) => (
                  <li key={p.linea}>
                    {p.jugador1} / {p.jugador2}
                    {p.cabeza_serie !== null ? ` · CS ${p.cabeza_serie}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {revision.errores.length > 0 && (
            <div>
              <p className="font-semibold text-(--color-alerta)">
                {revision.errores.length} renglón{revision.errores.length === 1 ? '' : 'es'} con problema (no se guarda{revision.errores.length === 1 ? '' : 'n'}):
              </p>
              <ul className="mt-1">
                {revision.errores.map((e) => (
                  <li key={`${e.linea}-${e.motivo}`} className="text-(--color-apagado)">
                    {e.linea > 0 && <span className="font-mono">«{e.texto}»</span>} — {e.motivo}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {revision && revision.parejas.length > 0 ? (
          <Boton chico onClick={() => mandar(false)} deshabilitado={enviando}>
            {enviando ? 'Guardando...' : `Inscribir ${revision.parejas.length}`}
          </Boton>
        ) : (
          <Boton chico onClick={() => mandar(true)} deshabilitado={enviando || !texto.trim()}>
            {enviando ? 'Revisando...' : 'Revisar'}
          </Boton>
        )}
        <Boton
          chico
          tipo="secundario"
          onClick={() => {
            setAbierto(false);
            setRevision(null);
            setGuardadas(0);
          }}
        >
          Cerrar
        </Boton>
      </div>
    </div>
  );
}
